import { reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, date, number, string, array } from 'yup'
import { useBudgets } from './useBudgets'
import { useBudgetDialog } from './useBudgetDialog'
import { useNotifications } from '@/services/notification/useNotifications'
import { useLogs } from '@/services/useLogs'
import { useLoader } from './useLoader'
import { useError } from './useError'
import dayjs from 'dayjs'
import axios from 'axios'

const tabs = reactive([
  {
    name: '予算詳細',
    required: true,
    fields: ['paymentDate', 'amount', 'contentIdList', 'remarks'],
  },
])

const items = reactive([
  { id: 0, name: '交付金' },
  { id: 1, name: '前期交付金' },
  { id: 2, name: '後期交付金' },
  { id: 3, name: '一部返還' },
  { id: 4, name: '決算利息' },
])
export const useBudgetFormTabs = () => {
  return { tabs }
}

const schema = object({
  paymentDate: date()
    .required('必須です')
    .typeError('必須です'),
  amount: number()
    .required('必須です')
    .integer('整数で入力してください')
    .typeError('必須です'),
  contentIdList: array()
    .min(1)
    .max(1)
    .required('必須です')
    .typeError('必須です'),
  remarks: string().required('必須です'),
})

export const useBudgetForm = () => {
  const { currentBudget, getBudgetsAfterResponse } = useBudgets()
  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { budgetDialog } = useBudgetDialog()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()
  watch(budgetDialog, () => {
    if (budgetDialog.isPreview) {
      currentBudget.paymentDate = paymentDate.value
      currentBudget.amount = amount.value
      currentBudget.contentIdList = [...contentIdList.value]
      currentBudget.remarks = remarks.value
    }
  })

  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  watch(meta, meta => {
    meta.valid
      ? (budgetDialog.isPreviewEnabled = true)
      : (budgetDialog.isPreviewEnabled = false)
  })

  const { value: paymentDate, errorMessage: paymentDateError } = useField(
    'paymentDate',
    undefined,
    {
      initialValue: currentBudget.paymentDate
        ? dayjs(currentBudget.paymentDate).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    },
  )

  const { value: amount, errorMessage: amountError } = useField(
    'amount',
    undefined,
    {
      initialValue: currentBudget.amount,
    },
  )

  const { value: contentIdList, errorMessage: contentIdListError } = useField(
    'contentIdList',
    undefined,
    {
      initialValue: currentBudget.contentIdList,
    },
  )

  const { value: remarks, errorMessage: remarksError } = useField(
    'remarks',
    undefined,
    {
      initialValue: currentBudget.remarks,
    },
  )

  // 予算ダイアログ「入力を確認する」ボタンを押したときにバリデーションを更新する。エラーがなければプレビュー画面へ
  const budgetSubmit = handleSubmit(isShown => {
    budgetDialog.isPreview = isShown
  })

  watch(paymentDate, () => {
    currentBudget.paymentDate = dayjs(paymentDate.value).format(
      'YYYY-MM-DD HH:mm:ss',
    )
    setStatus(0)
  })
  watch(amount, () => {
    currentBudget.amount = amount.value
    setStatus(0)
  })
  watch(contentIdList, () => {
    currentBudget.contentIdList = contentIdList.value
    setStatus(0)
  })
  watch(remarks, () => {
    currentBudget.remarks = remarks.value
    setStatus(0)
  })

  // validateのタイミングでタブのstatusを更新する
  watch(paymentDateError, () => {
    setStatus(0)
  })
  watch(amountError, () => {
    setStatus(0)
  })
  watch(contentIdListError, () => {
    setStatus(0)
  })
  watch(remarksError, () => {
    setStatus(0)
  })

  const setStatus = index => {
    const err = error(index)
    const inp = input(index)
    if (err && inp) {
      tabs[index].status = 1
    } else if (err && !inp) {
      tabs[index].status = 0
    } else if (!err) {
      tabs[index].status = -1
    }
  }

  // エラーがある時falseを返す
  const error = index => {
    let err = false

    if (index === 0) {
      err =
        typeof paymentDateError.value === 'undefined' &&
        typeof amountError.value === 'undefined' &&
        typeof contentIdListError.value === 'undefined' &&
        typeof remarksError.value === 'undefined'
    }
    return err
  }

  const input = index => {
    let inp = false
    if (index === 0) {
      inp = paymentDate.value && amount.value && contentIdList.value && remarks
    }
    return inp
  }

  /**
   * 予算を更新する\
   * 成功した場合、画面の予算リストを更新する
   *
   * @param {number} budgetId budgets.id
   */
  const updateBudget = async budgetId => {
    try {
      toggleShowLoader(true)
      const payload = {
        content_id: currentBudget.contentIdList[0].id,
        amount: currentBudget.amount,
        payment_date: currentBudget.paymentDate,
        remarks: currentBudget.remarks ?? ' ',
      }
      const response = await axios.put(`/budgets/${budgetId}`, payload)
      await getBudgetsAfterResponse(response)
      if (route.name === 'Logs') {
        await getTimelineAfterResponse(response)
      } else if (route.name === 'Notifications') {
        await getNotificationsAfterResponse(response)
      }
      toggleShowLoader(false)
    } catch (err) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 予算を作成する\
   * 成功した場合、画面の予算リストを取得する
   */
  const createBudget = async () => {
    try {
      toggleShowLoader(true)
      const payload = {
        content_id: currentBudget.contentIdList[0].id,
        amount: currentBudget.amount,
        payment_date: currentBudget.paymentDate,
        remarks: currentBudget.remarks,
      }
      const response = await axios.post(`/budgets`, payload)
      await getBudgetsAfterResponse(response)
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  /**
   * 予算を新規追加または更新する
   *
   * @param {number} budgetId budgets.id nullの場合POST
   */
  const submitBudget = async (budgetId = null) => {
    // validate
    if (
      !(budgetId === null || (typeof budgetId === 'number' && budgetId > 0))
    ) {
      throw new Error('invalid budgetId: ' + budgetId)
    }

    // execute
    const isPost = budgetId === null
    if (isPost) {
      await createBudget()
    } else {
      await updateBudget(budgetId)
    }
  }

  return {
    handleSubmit,
    budgetSubmit,
    submitBudget,
    items,
    tabs,
    paymentDate,
    paymentDateError,
    amount,
    amountError,
    contentIdList,
    contentIdListError,
    remarks,
    remarksError,
  }
}
